import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch {
	jsPDF = null;
}

const theme = {
	primary: [155, 27, 42],
	primaryDark: [107, 15, 26],
	bg: [245, 247, 250],
	border: [229, 231, 235],
	text: [30, 41, 59],
	muted: [100, 116, 139],
	white: [255, 255, 255],
	success: [22, 163, 74],
	warn: [217, 119, 6],
	danger: [220, 38, 38],
};

const SP = {pagePad: 42, blockGap: 12, cardPad: 12, footerH: 44};

const safeText = (v, fb = "—") => (v == null || v === "" ? fb : String(v));
const fmt = (v, d = 1) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));

const mean = (values) => {
	const v = values.filter((x) => x != null);
	return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

// Dati del report dalla fotografia del club della vista DS (club intero)
export const briefFromSnapshot = (s) => {
	const wellness = s.teams.flatMap((t) => t.wellness.players);
	const expiring = (from, to) =>
		s.contracts.filter(
			(c) => c.daysLeft !== null && c.daysLeft >= from && c.daysLeft <= to,
		);
	return {
		weekLabel:
			moment().subtract(6, "days").format("DD/MM") +
			" — " +
			moment().format("DD/MM/YYYY"),
		generatedAt: moment().format("DD/MM/YYYY HH:mm"),
		totalPlayers: s.totalPlayers,
		totalRosters: s.teams.length,
		globalTQR: mean(wellness.map((w) => w.avgTQR)),
		globalRPE: mean(wellness.map((w) => w.avgRPE)),
		totalInjured: s.activeInjuries.length,
		missingSources: s.missingSources,
		contracts30: expiring(0, 30),
		contracts90: expiring(31, 90),
		contracts180: expiring(91, 180),
		rosterBriefs: s.teams.map((t) => ({
			name: t.name,
			playersCount: t.players.length,
			avgTQR: t.wellness.avgTQR,
			avgRPE: t.wellness.avgRPE,
			lowTQRCount: t.wellness.lowTQR.length,
			highRPECount: t.wellness.highRPE.length,
			injuredCount: t.injured.length,
			noDataCount: t.wellness.players.filter(
				(w) => w.avgTQR === null && w.avgRPE === null,
			).length,
		})),
		activeInjuries: s.activeInjuries.map((inj) => ({
			...inj,
			playerName: [inj.player?.firstName, inj.player?.lastName]
				.filter(Boolean)
				.join(" "),
		})),
	};
};

export const generateDecisionBriefPDF = (brief) => {
	if (!jsPDF) throw new Error("jsPDF non disponibile");
	if (!brief) throw new Error("Dati brief mancanti");

	const doc = new jsPDF({orientation: "portrait", unit: "pt", format: "a4"});
	const pageW = doc.internal.pageSize.getWidth();
	const pageH = doc.internal.pageSize.getHeight();
	const contentW = pageW - SP.pagePad * 2;
	let y = 0;

	const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);
	const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
	const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);

	const checkPageBreak = (needed) => {
		if (y + needed > pageH - SP.footerH) {
			doc.addPage();
			y = SP.pagePad;
			return true;
		}
		return false;
	};

	const drawCard = (cx, cy, cw, ch, opts = {}) => {
		const r = opts.radius ?? 6;
		setFill(opts.fill ?? theme.white);
		setDraw(opts.border ?? theme.border);
		doc.setLineWidth(0.5);
		doc.roundedRect(cx, cy, cw, ch, r, r, "FD");
	};

	const drawSectionTitle = (title) => {
		checkPageBreak(30);
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(10);
		setColor(theme.primary);
		doc.text(title.toUpperCase(), SP.pagePad, y);
		y += 4;
		setDraw(theme.primary);
		doc.setLineWidth(1.5);
		doc.line(SP.pagePad, y, SP.pagePad + 40, y);
		y += SP.blockGap;
	};

	// ── Header ────────────────────────────────────────────────────────────────

	const headerH = 78;
	setFill(theme.primaryDark);
	doc.rect(0, 0, pageW, headerH, "F");
	setFill(theme.primary);
	doc.rect(0, headerH - 3, pageW, 3, "F");

	doc.setFont("Helvetica", "bold");
	doc.setFontSize(22);
	setColor(theme.white);
	doc.text("REPORT SETTIMANALE", SP.pagePad, 36);

	doc.setFont("Helvetica", "normal");
	doc.setFontSize(11);
	doc.setTextColor(200, 200, 210);
	doc.text(`Settimana ${brief.weekLabel}`, SP.pagePad, 54);

	doc.setFont("Helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(180, 180, 190);
	const genStr = `Generato il ${brief.generatedAt}`;
	doc.text(genStr, pageW - SP.pagePad - doc.getTextWidth(genStr), 54);

	y = headerH + SP.blockGap + 8;

	// ── KPI strip ─────────────────────────────────────────────────────────────

	const kpis = [
		{label: "GIOCATORI", value: String(brief.totalPlayers)},
		{label: "ROSE", value: String(brief.totalRosters)},
		{label: "TQR MEDIO", value: fmt(brief.globalTQR)},
		{label: "RPE MEDIO", value: fmt(brief.globalRPE)},
		{label: "INFORTUNATI", value: safeText(brief.totalInjured)},
	];

	const kpiH = 46;
	const kpiW = contentW / kpis.length;
	drawCard(SP.pagePad, y, contentW, kpiH, {fill: theme.bg});

	kpis.forEach((kpi, i) => {
		const cx = SP.pagePad + i * kpiW + kpiW / 2;

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(18);
		setColor(theme.text);
		const vw = doc.getTextWidth(kpi.value);
		doc.text(kpi.value, cx - vw / 2, y + 20);

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(7);
		setColor(theme.muted);
		const lw = doc.getTextWidth(kpi.label);
		doc.text(kpi.label, cx - lw / 2, y + 34);

		if (i < kpis.length - 1) {
			setDraw(theme.border);
			doc.setLineWidth(0.5);
			doc.line(
				SP.pagePad + (i + 1) * kpiW,
				y + 8,
				SP.pagePad + (i + 1) * kpiW,
				y + kpiH - 8,
			);
		}
	});

	y += kpiH + SP.blockGap;
	if (brief.missingSources?.length) {
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(8);
		setColor(theme.warn);
		const lines = doc.splitTextToSize(
			`Riepilogo parziale. Dati non disponibili: ${brief.missingSources.join(", ")}.`,
			contentW,
		);
		doc.text(lines, SP.pagePad, y);
		y += lines.length * 10 + SP.blockGap;
	}

	// ── Contratti ─────────────────────────────────────────────────────────────

	const allContracts = [
		...(brief.contracts30 || []).map((c) => ({...c, urgency: "critical"})),
		...(brief.contracts90 || []).map((c) => ({...c, urgency: "warning"})),
		...(brief.contracts180 || []).map((c) => ({...c, urgency: "safe"})),
	];

	if (allContracts.length) {
		drawSectionTitle("Contratti in scadenza");

		const rowH = 20;
		const cardH = allContracts.length * rowH + SP.cardPad * 2;
		checkPageBreak(cardH + 20);
		drawCard(SP.pagePad, y, contentW, cardH);

		allContracts.forEach((c, i) => {
			const ry = y + SP.cardPad + i * rowH;
			if (i % 2 === 0) {
				setFill(theme.bg);
				doc.rect(SP.pagePad + 1, ry, contentW - 2, rowH, "F");
			}

			const dotColor =
				c.urgency === "critical"
					? theme.danger
					: c.urgency === "warning"
						? theme.warn
						: theme.success;
			setFill(dotColor);
			doc.circle(SP.pagePad + SP.cardPad + 4, ry + rowH / 2, 3, "F");

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(9);
			setColor(theme.text);
			doc.text(
				`${c.playerFirstName || c.firstName || ""} ${c.playerLastName || c.lastName || ""}`.trim(),
				SP.pagePad + SP.cardPad + 14,
				ry + 13,
			);

			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			const dateStr = c.endDate ? moment(c.endDate).format("DD/MM/YYYY") : "—";
			const dateW = doc.getTextWidth(dateStr);
			doc.text(dateStr, pageW - SP.pagePad - SP.cardPad - dateW, ry + 13);
		});

		y += cardH + SP.blockGap;
	}

	// ── Stato per rosa ────────────────────────────────────────────────────────

	if (brief.rosterBriefs?.length) {
		drawSectionTitle("Stato per rosa");

		brief.rosterBriefs.forEach((r) => {
			const flags = [];
			if (r.lowTQRCount > 0) flags.push(`${r.lowTQRCount} TQR basso`);
			if (r.highRPECount > 0) flags.push(`${r.highRPECount} RPE alto`);
			if (r.injuredCount > 0) flags.push(`${r.injuredCount} infortunati`);
			if (r.expiredCertsCount > 0)
				flags.push(`${r.expiredCertsCount} doc. scaduti`);
			if (r.noDataCount > 0) flags.push(`${r.noDataCount} senza dati`);

			const rosterH = 36 + (flags.length ? 14 : 0);
			checkPageBreak(rosterH + 10);
			drawCard(SP.pagePad, y, contentW, rosterH);

			// Nome rosa
			doc.setFont("Helvetica", "bold");
			doc.setFontSize(11);
			setColor(theme.text);
			doc.text(r.name, SP.pagePad + SP.cardPad, y + 16);

			// Count
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			const countStr = `${r.playersCount} giocatori`;
			const cw = doc.getTextWidth(countStr);
			doc.text(countStr, pageW - SP.pagePad - SP.cardPad - cw, y + 16);

			// TQR + RPE
			const tqrColor =
				r.avgTQR >= 15
					? theme.success
					: r.avgTQR >= 13
						? theme.warn
						: r.avgTQR != null
							? theme.danger
							: theme.muted;
			const rpeColor =
				r.avgRPE <= 4
					? theme.success
					: r.avgRPE <= 7
						? theme.warn
						: r.avgRPE != null
							? theme.danger
							: theme.muted;

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(8);
			setColor(theme.muted);
			doc.text("TQR", SP.pagePad + SP.cardPad, y + 30);
			doc.setFontSize(10);
			setColor(tqrColor);
			doc.text(fmt(r.avgTQR), SP.pagePad + SP.cardPad + 24, y + 30);

			setColor(theme.muted);
			doc.setFontSize(8);
			doc.text("RPE", SP.pagePad + SP.cardPad + 60, y + 30);
			doc.setFontSize(10);
			setColor(rpeColor);
			doc.text(fmt(r.avgRPE), SP.pagePad + SP.cardPad + 84, y + 30);

			// Flags
			if (flags.length) {
				doc.setFont("Helvetica", "normal");
				doc.setFontSize(8);
				setColor(theme.muted);
				doc.text(flags.join("  ·  "), SP.pagePad + SP.cardPad + 140, y + 30);
			}

			y += rosterH + 4;
		});

		y += SP.blockGap;
	}

	// ── Infortuni attivi ──────────────────────────────────────────────────────

	if (brief.activeInjuries?.length) {
		drawSectionTitle(`Infortuni attivi (${brief.activeInjuries.length})`);

		const injRowH = 22;
		const injCardH = brief.activeInjuries.length * injRowH + SP.cardPad * 2;
		checkPageBreak(injCardH + 20);
		drawCard(SP.pagePad, y, contentW, injCardH);

		brief.activeInjuries.forEach((inj, i) => {
			const ry = y + SP.cardPad + i * injRowH;
			if (i % 2 === 0) {
				setFill(theme.bg);
				doc.rect(SP.pagePad + 1, ry, contentW - 2, injRowH, "F");
			}

			setFill(theme.danger);
			doc.circle(SP.pagePad + SP.cardPad + 4, ry + injRowH / 2, 3, "F");

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(9);
			setColor(theme.text);
			doc.text(safeText(inj.playerName), SP.pagePad + SP.cardPad + 14, ry + 14);

			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			const diagnosis = safeText(inj.diagnosis || inj.injuryType, "Infortunio");
			doc.text(diagnosis, SP.pagePad + SP.cardPad + 140, ry + 14);

			if (inj.sportsDischargeDate) {
				const daysToReturn = moment(inj.sportsDischargeDate).diff(
					moment(),
					"days",
				);
				const retStr =
					daysToReturn >= 0 ? `Rientro tra ${daysToReturn}gg` : "Rientro N/D";
				const retW = doc.getTextWidth(retStr);
				setColor(
					daysToReturn >= 0 && daysToReturn <= 7 ? theme.success : theme.muted,
				);
				doc.text(retStr, pageW - SP.pagePad - SP.cardPad - retW, ry + 14);
			}
		});

		y += injCardH + SP.blockGap;
	}

	// ── Footer ────────────────────────────────────────────────────────────────

	const totalPages = doc.internal.getNumberOfPages();
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p);

		setDraw(theme.border);
		doc.setLineWidth(0.5);
		doc.line(
			SP.pagePad,
			pageH - SP.footerH + 10,
			pageW - SP.pagePad,
			pageH - SP.footerH + 10,
		);

		doc.setFont("Helvetica", "normal");
		doc.setFontSize(8);
		setColor(theme.muted);
		doc.text(`Generato il ${brief.generatedAt}`, SP.pagePad, pageH - 20);

		const pageStr = `Pagina ${p} / ${totalPages}`;
		doc.text(
			pageStr,
			pageW - SP.pagePad - doc.getTextWidth(pageStr),
			pageH - 20,
		);

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(7);
		setColor(theme.primary);
		const brand = "FOOTBALL DECISION OS";
		doc.text(brand, pageW / 2 - doc.getTextWidth(brand) / 2, pageH - 20);
	}

	doc.save(`Report_settimanale_${moment().format("YYYY-MM-DD")}.pdf`);
};
